import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Calendar, Settings, User, Users, Eye, EyeOff, ArrowLeftRight } from 'lucide-react';
import Weekrooster from './pages/Weekrooster.jsx';
import Beheer from './pages/Beheer.jsx';
import MijnRooster from './pages/MijnRooster.jsx';
import TeamRooster from './pages/TeamRooster.jsx';
import Ruilverzoeken from './pages/Ruilverzoeken.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import LoginKnop from './components/LoginKnop.jsx';
import MeldingBel from './components/MeldingBel.jsx';
import OmgevingKiezer from './components/OmgevingKiezer.jsx';
import MobielMenu from './components/MobielMenu.jsx';
import { useRol } from './auth/useRol.js';
import { meldSessieVerlopen, meldDataConflict } from './auth/huidigeGebruiker.js';
import { loginRequest } from './auth/msalConfig.js';
import { haalRuilverzoeken, haalNieuweLogs, LOGS_GEZIEN_EVENT } from './lib/api.js';

const NAV_TEAMLEIDER = [
  { to: '/', label: 'Rooster', icon: Calendar },
  { to: '/beheer', label: 'Beheer', icon: Settings },
  { to: '/teamrooster', label: 'Teamrooster', icon: Users },
  { to: '/mijn-rooster', label: 'Mijn Rooster', icon: User },
  { to: '/ruilverzoeken', label: 'Ruilverzoeken', icon: ArrowLeftRight },
];

const NAV_MEDEWERKER = [
  { to: '/teamrooster', label: 'Teamrooster', icon: Users },
  { to: '/mijn-rooster', label: 'Mijn Rooster', icon: User },
  { to: '/ruilverzoeken', label: 'Ruilverzoeken', icon: ArrowLeftRight },
];

function Header({ children }) {
  return (
    <header className="relative bg-brand-700 text-white shadow-lg">
      <div className="max-w-6xl mx-auto px-4 py-3 lg:py-4 flex flex-wrap items-center justify-between gap-y-2">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="SASS logo" className="w-10 h-10 object-contain" />
          <div>
            <div className="font-bold text-sm leading-none">SASS Rooster</div>
            <div className="text-xs leading-none mt-0.5 text-brand-100">Straat en Schoolveiligheid</div>
          </div>
        </div>
        <div className="flex items-center gap-4">{children}</div>
      </div>
    </header>
  );
}

// `badges` is een object per route ({ '/ruilverzoeken': 3, '/beheer': 1 }): zo kan elk
// nav-item een teller krijgen zonder dat NavBalk elke route bij naam hoeft te kennen.
// Ontbrekend of 0 = geen bolletje.
function NavBalk({ items, badges = {} }) {
  return (
    <nav className="flex">
      {items.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={to === '/'}
          className={({ isActive }) =>
            `flex items-center gap-2 px-4 py-5 text-sm font-medium border-b-2 transition-colors ${
              isActive ? 'text-white border-accent-500' : 'text-brand-100 border-transparent hover:text-white'
            }`
          }
        >
          <Icon className="w-4 h-4" />
          {label}
          {badges[to] > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-xs font-semibold leading-none">
              {badges[to] > 9 ? '9+' : badges[to]}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

// Onzichtbare helper BINNEN de Router: telt "actie nodig" voor de nav-badge en ververst bij
// elke navigatie (location.pathname-wissel). Defensief: bij een fout of niet-ingelogd → 0.
// Moet in de Router-boom staan (useLocation), want App() rendert zelf de <BrowserRouter>.
function RuilBadgeWatcher({ actief, isTeamleider, alsMedewerker, onCount }) {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!actief) { onCount(0); return; }
    let afgebroken = false;
    haalRuilverzoeken()
      .then((res) => {
        if (afgebroken) return;
        const mwKey = res.mwKey;
        const mijn = (res.mijnVerzoeken || []).filter((v) => v.collegaKey === mwKey && v.status === 'voorgesteld').length;
        const beoordelen = (isTeamleider && !alsMedewerker) ? (res.teBeoordelen || []).length : 0;
        const kapot = (isTeamleider && !alsMedewerker) ? (res.kapotteRuilen || []).length : 0;
        onCount(mijn + beoordelen + kapot);
      })
      .catch(() => { if (!afgebroken) onCount(0); });
    return () => { afgebroken = true; };
  }, [pathname, actief, isTeamleider, alsMedewerker, onCount]);
  return null;
}

// Zelfde model als RuilBadgeWatcher, maar voor het foutenlogboek: hoeveel logregels zijn er
// nieuw sinds DEZE teamleider het logboek opende. Alleen actief voor de échte teamleider —
// /api/logs/nieuw is teamleider-only, dus voor een medewerker (of een teamleider in preview)
// zou elke navigatie een 403 opleveren. Defensief: bij een fout of niet-actief → 0.
//
// Twee triggers, want één is niet genoeg: het logboek is een TAB binnen Beheer, dus wie het
// opent navigeert niet en het pathname-effect vuurt dan niet. LogViewer geeft daarom na een
// geslaagd gezien-stempel het LOGS_GEZIEN_EVENT af; het antwoord is op dat moment per
// definitie 0, dus de badge kan direct weg zonder extra fetch.
function LogBadgeWatcher({ actief, onCount }) {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!actief) { onCount(0); return; }
    let afgebroken = false;
    haalNieuweLogs()
      .then((res) => { if (!afgebroken) onCount(res?.aantal || 0); })
      .catch(() => { if (!afgebroken) onCount(0); });
    return () => { afgebroken = true; };
  }, [pathname, actief, onCount]);

  useEffect(() => {
    const opGezien = () => onCount(0);
    window.addEventListener(LOGS_GEZIEN_EVENT, opGezien);
    return () => window.removeEventListener(LOGS_GEZIEN_EVENT, opGezien);
  }, [onCount]);

  return null;
}

// Sessie verlopen: één centrale, blokkerende overlay. Ligt BOVENOP de app (die blijft gewoon
// staan, dus er gaat niets verloren) en biedt de enige zinnige uitweg: opnieuw inloggen.
// Zelfde redirect-flow als LoginKnop; een popup zou op mobiel geblokkeerd worden.
function SessieVerlopenOverlay() {
  const { instance, inProgress } = useMsal();
  const bezig = inProgress !== InteractionStatus.None;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-8 w-full max-w-sm text-center">
        <div className="text-4xl mb-3">🔒</div>
        <p className="text-slate-700 font-medium">Je sessie is verlopen — log opnieuw in.</p>
        <button
          onClick={() => instance.loginRedirect(loginRequest)}
          disabled={bezig}
          className="mt-5 w-full px-4 py-2 rounded-lg bg-brand-700 text-white text-sm font-medium hover:bg-brand-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Opnieuw inloggen
        </button>
      </div>
    </div>
  );
}

// Data-conflict: een andere teamleider heeft intussen geschreven (409 op PUT /api/data).
// Zelfde blokkerende opzet als SessieVerlopenOverlay hierboven — bewust GEEN keuze om toch
// door te zetten: doorwerken op een verouderde stand is precies wat we wilden voorkomen, en
// de lokale wijziging is niet te redden zonder de andere teamleider zijn werk af te pakken.
// Herladen is de enige uitweg; dat trekt de verse serverdata binnen via laadVanServer.
function DataConflictOverlay({ info }) {
  const wanneer = info?.tijdstip ? new Date(info.tijdstip) : null;
  const wanneerTekst = wanneer && !Number.isNaN(wanneer.getTime())
    ? wanneer.toLocaleString('nl-NL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-8 w-full max-w-sm text-center">
        <div className="text-4xl mb-3">🔄</div>
        <p className="text-slate-700 font-medium">Het rooster is inmiddels gewijzigd.</p>
        <p className="mt-2 text-sm text-slate-500">
          {info?.doorNaam ? `${info.doorNaam} heeft` : 'Iemand anders heeft'}
          {wanneerTekst ? ` om ${wanneerTekst}` : ''} opgeslagen terwijl jij bezig was.
          Je laatste wijziging is niet bewaard.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="mt-5 w-full px-4 py-2 rounded-lg bg-brand-700 text-white text-sm font-medium hover:bg-brand-800 transition-colors"
        >
          Rooster opnieuw laden
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const [sessieVerlopen, setSessieVerlopen] = useState(false);
  const [dataConflict, setDataConflict] = useState(null);

  // Eén melder voor de hele app: api.js vuurt 'm af bij een 401, huidigeGebruiker.js bij een
  // InteractionRequiredAuthError. Zo hoeft geen enkele pagina de technische fout meer te tonen.
  useEffect(() => {
    meldSessieVerlopen(() => setSessieVerlopen(true));
    return () => meldSessieVerlopen(null);
  }, []);

  // Zelfde opzet voor het schrijfconflict: api.js vuurt 'm af bij een 409 op PUT /api/data.
  useEffect(() => {
    meldDataConflict((info) => setDataConflict(info));
    return () => meldDataConflict(null);
  }, []);

  return (
    <>
      <AppInhoud />
      {sessieVerlopen && <SessieVerlopenOverlay />}
      {dataConflict && <DataConflictOverlay info={dataConflict} />}
    </>
  );
}

function AppInhoud() {
  const { status, isTeamleider, isRoosterbaar, omgevingen, actieveOmgeving } = useRol();
  const [alsMedewerker, setAlsMedewerker] = useState(false);
  const [ruilBadge, setRuilBadge] = useState(0);
  const [logBadge, setLogBadge] = useState(0);

  // Niet ingelogd: alleen een inlogscherm, geen nav.
  if (status === 'uit') {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header><LoginKnop /></Header>
        <main className="max-w-6xl mx-auto px-4 py-20 text-center">
          <p className="text-slate-600 text-lg">Log in met je Microsoft-werkaccount om je rooster te zien.</p>
        </main>
      </div>
    );
  }

  // Rol wordt nog geladen.
  if (status === 'laden') {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header><LoginKnop /></Header>
        <main className="max-w-6xl mx-auto px-4 py-20 text-center">
          <p className="text-slate-500">Moment, je rol wordt geladen...</p>
        </main>
      </div>
    );
  }

  // Server onbereikbaar of andere fout bij het ophalen van de rol.
  if (status === 'fout') {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header><LoginKnop /></Header>
        <main className="max-w-6xl mx-auto px-4 py-20 text-center">
          <p className="text-slate-600">Je rol kon niet worden opgehaald. Draait de server? Probeer het zo opnieuw.</p>
        </main>
      </div>
    );
  }

  // Ingelogd + rol bekend. Teamleider die niet in preview staat ziet de volledige nav.
  const toonTeamleider = isTeamleider && !alsMedewerker;
  // Verberg 'Mijn Rooster' voor wie zelf niet wordt ingeroosterd (teamleider-only). Geldt voor
  // BEIDE navs, dus ook in de "Bekijk als medewerker"-preview.
  const navItems = (toonTeamleider ? NAV_TEAMLEIDER : NAV_MEDEWERKER)
    .filter((item) => item.to !== '/mijn-rooster' || isRoosterbaar);
  // Eén tellerslijst voor beide navs (brede balk én hamburger), zodat ze niet uit elkaar lopen.
  const badges = { '/ruilverzoeken': ruilBadge, '/beheer': logBadge };

  return (
    <BrowserRouter>
      <RuilBadgeWatcher
        actief={status === 'klaar'}
        isTeamleider={isTeamleider}
        alsMedewerker={alsMedewerker}
        onCount={setRuilBadge}
      />
      <LogBadgeWatcher actief={toonTeamleider} onCount={setLogBadge} />
      <div className="min-h-screen bg-slate-50">
        <Header>
          {/* Vanaf lg: exact de huidige cluster (nav + omgeving + preview-knop). Onder lg verborgen. */}
          <div className="hidden lg:flex items-center gap-4">
            <NavBalk items={navItems} badges={badges} />
            <OmgevingKiezer omgevingen={omgevingen} actieveOmgeving={actieveOmgeving} />
            {isTeamleider && (
              <button
                onClick={() => setAlsMedewerker((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-brand-100 hover:text-white hover:bg-white/10 transition-colors"
              >
                {alsMedewerker ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {alsMedewerker ? 'Terug naar teamleider' : 'Bekijk als medewerker'}
              </button>
            )}
          </div>

          {/* MeldingBel blijft op elk scherm direct zichtbaar (klein). */}
          <MeldingBel />

          {/* Vanaf lg: Uitloggen naast de bel, zoals nu. */}
          <div className="hidden lg:block">
            <LoginKnop />
          </div>

          {/* Onder lg: hamburger met het paneel; nav-items + omgeving + preview-knop + uitloggen. */}
          <div className="lg:hidden">
            <MobielMenu navItems={navItems} badges={badges}>
              <OmgevingKiezer omgevingen={omgevingen} actieveOmgeving={actieveOmgeving} />
              {isTeamleider && (
                <button
                  onClick={() => setAlsMedewerker((v) => !v)}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-brand-100 hover:text-white hover:bg-white/10 transition-colors"
                >
                  {alsMedewerker ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {alsMedewerker ? 'Terug naar teamleider' : 'Bekijk als medewerker'}
                </button>
              )}
              <LoginKnop />
            </MobielMenu>
          </div>
        </Header>

        {isTeamleider && alsMedewerker && (
          <div className="bg-accent-500 text-white text-sm text-center py-2">
            Je bekijkt de omgeving als medewerker.
          </div>
        )}

        <main className="max-w-6xl mx-auto px-4 py-8">
          <ErrorBoundary>
            <Routes>
              {toonTeamleider ? (
                <>
                  <Route path="/" element={<Weekrooster />} />
                  <Route path="/beheer" element={<Beheer />} />
                </>
              ) : (
                <>
                  <Route path="/" element={<Navigate to="/mijn-rooster" replace />} />
                  <Route path="/beheer" element={<Navigate to="/mijn-rooster" replace />} />
                </>
              )}
              <Route path="/teamrooster" element={<TeamRooster />} />
              {/* Niet roosterbaar (teamleider-only) → geen eigen rooster: redirect i.p.v. een lege
                  pagina. Teamleider terug naar '/', in medewerker-preview naar het teamrooster —
                  nooit naar '/mijn-rooster' zelf, want met de '/'-redirect zou dat een lus vormen. */}
              <Route path="/mijn-rooster" element={isRoosterbaar ? <MijnRooster /> : <Navigate to={toonTeamleider ? '/' : '/teamrooster'} replace />} />
              <Route path="/ruilverzoeken" element={<Ruilverzoeken previewAlsMedewerker={alsMedewerker} />} />
              <Route path="*" element={<Navigate to={toonTeamleider ? '/' : '/mijn-rooster'} replace />} />
            </Routes>
          </ErrorBoundary>
        </main>

        <footer className="border-t border-slate-200 mt-12">
          <div className="max-w-6xl mx-auto px-4 py-4 text-xs text-slate-400 flex justify-between">
            <span>SASS Rooster</span>
            <span>© Stichting Aanpak Straat- &amp; Schoolveiligheid</span>
          </div>
        </footer>
      </div>
    </BrowserRouter>
  );
}
